import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
    faArrowToBottom,
    faChevronLeft,
    faChevronRight,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { useRef, useState } from "react";
import { PaymentRequestStatus } from "../../../../PaymentContainerClient/commons/enums";
import DocumentDto from "../../../../PaymentContainerClient/dtos/DocumentDto";
import PaymentRequestDto from "../../../../PaymentContainerClient/dtos/PaymentRequestDto";
import getFormattedMessageWithScope from "../../../../utils/getFormattedMessageWithScope";
import { CustomButton } from "../../CustomButton";
import { CustomMenuPopover } from "../../CustomMenuPopover";
import { useWindowDimensions } from "../../WindowDimensionsProvider";
import "./style.css";

const fm = getFormattedMessageWithScope("components.DocumentsPreviewer");

interface DocumentsActionsToolbarProps {
    canChangeDocument: boolean;
    documents: DocumentDto[];
    selectedDocument?: DocumentDto;
    isDownloading: boolean;
    onChangeSelectedDocument: Function;
    onChangeSelectedDocumentById: Function;
    onDownload: Function;
    paymentRequest: PaymentRequestDto;
    documentIdDownloading?: string;
}

export const DocumentsActionsToolbar = ({
    canChangeDocument,
    documents,
    onChangeSelectedDocument,
    onChangeSelectedDocumentById,
    onDownload,
    selectedDocument,
    isDownloading,
    paymentRequest,
    documentIdDownloading,
}: DocumentsActionsToolbarProps) => {
    const { width } = useWindowDimensions();
    const documentsMenuRef = useRef(null);
    const [isDocumentsMenuOpen, setIsDocumentsMenuOpen] = useState(false);
    const [menuButtonPosition, setMenuButtonPosition] = useState({
        left: 0,
        top: 0,
    });

    const isDownloadDocumentBlocked =
        !selectedDocument?.allowDownloadBeforePayment &&
        paymentRequest.status !== PaymentRequestStatus.Paid;

    function toggleDocumentsMenu() {
        setIsDocumentsMenuOpen(!isDocumentsMenuOpen);
        if (documentsMenuRef.current) {
            const boundingRect = (
                documentsMenuRef.current as any
            ).getBoundingClientRect();

            setMenuButtonPosition({
                left: boundingRect.left,
                top: boundingRect.top,
            });
        }
    }

    function selectDocument(docId: string) {
        toggleDocumentsMenu();
        onChangeSelectedDocumentById(docId);
    }

    return (
        <>
            <div className="c-DocumentsPreviewer-DocumentsActionsToolbar">
                <div className="c-DocumentsPreviewer-DocumentsActionsToolbar-documents-selection">
                    <button
                        className={classNames(
                            "c-DocumentsPreviewer-DocumentsActionsToolbar-documents-selection-button",
                            !canChangeDocument ? "disabled" : undefined
                        )}
                        onClick={() => onChangeSelectedDocument(-1)}
                        disabled={!canChangeDocument}
                    >
                        <FontAwesomeIcon icon={faChevronLeft as IconProp} />
                    </button>
                    <span
                        className="c-DocumentsPreviewer-DocumentsActionsToolbar-documents-selection-file-description"
                        onClick={toggleDocumentsMenu}
                        ref={documentsMenuRef}
                    >
                        {selectedDocument?.description}
                    </span>
                    <button
                        className={classNames(
                            "c-DocumentsPreviewer-DocumentsActionsToolbar-documents-selection-button",
                            !canChangeDocument ? "disabled" : undefined
                        )}
                        onClick={() => onChangeSelectedDocument(1)}
                        disabled={!canChangeDocument}
                    >
                        <FontAwesomeIcon icon={faChevronRight as IconProp} />
                    </button>
                </div>
                <div className="c-DocumentsPreviewer-DocumentsActionsToolbar-download-container">
                    <CustomButton
                        kind="tertiary"
                        onClick={() => onDownload(selectedDocument!)}
                        loading={
                            isDownloading &&
                            selectedDocument!.documentId ===
                                documentIdDownloading
                        }
                        disabled={isDownloadDocumentBlocked || isDownloading}
                        tooltipText={
                            isDownloadDocumentBlocked
                                ? fm("downloadBoundToPayment")
                                : undefined
                        }
                        icon={
                            <FontAwesomeIcon
                                icon={faArrowToBottom as IconProp}
                                className="c-DocumentsPreviewer-DocumentsActionsToolbar-download-icon"
                            />
                        }
                    >
                        {width > 800 ? fm("download") : undefined}
                    </CustomButton>
                </div>
            </div>
            <CustomMenuPopover
                visible={isDocumentsMenuOpen}
                position={menuButtonPosition}
                onClose={toggleDocumentsMenu}
                items={documents.map((doc) => ({
                    id: doc.documentId,
                    label: doc.description,
                    disabled:
                        !doc.allowDownloadBeforePayment &&
                        paymentRequest.status !== PaymentRequestStatus.Paid,
                }))}
                onSelectItem={selectDocument}
            />
        </>
    );
};
